/**
 * #344 — pure submit-payload builders for the Game Detail order ticket.
 *
 * Extracted verbatim from `OrderEntryForm.handleSubmit` / `handleOscarFire`.
 * Nothing here reads a clock, a store, or the DOM: `resolveExpiry` takes
 * `nowMs`, which is the whole reason the payload tests need no mocks.
 *
 * The optional keys are spread conditionally on TRUTHINESS, not on nullishness.
 * That is deliberate and load-bearing at the venue: `expirationTs: 0` means
 * "GTC" to the relay, and sending the key with a 0 would be read as an expiry
 * at the epoch. Same for an empty `tickSize`.
 */
import type { OrderInput } from '@/types/kalshi';
import type { PlacePolyOrderRelayArgs } from '@/lib/polymarket/relay';
import type { PlacePolymarketUsOrderArgs } from '@/lib/polymarketUs/relay';
import { validateLeg, type LadderLeg } from './orderLadder';
import type { MarketSide, TeamSide } from './types';

// ── Expiry ────────────────────────────────────────────────────────────────

export type ExpiryResolution =
  | { ok: true; expirationMs: number | undefined }
  | { ok: false; error: string };

/**
 * Resolve the ticket's expiry choice to epoch ms, or to the message the result
 * line shows when it cannot. Callers derive the venue wire format from the ms.
 *
 * GTS is a safety-bearing trader instruction, not a presentation preference.
 * If the selected ticket no longer has a trustworthy FUTURE start, fail closed
 * and require the trader to choose GTC explicitly. Silently omitting the expiry
 * turns a bounded resting order into an unbounded one.
 */
export function resolveExpiry(args: {
  expiryMode: 'gts' | 'gtc' | 'custom';
  gameStartMs: number | null;
  customExpiry: string;
  nowMs: number;
}): ExpiryResolution {
  const { expiryMode, gameStartMs, customExpiry, nowMs } = args;
  if (expiryMode === 'gts') {
    if (gameStartMs == null || gameStartMs <= nowMs) {
      return {
        ok: false,
        error:
          'Good to Start is unavailable because a trustworthy future event start is not available. Choose Good to Close explicitly to place without an expiry.',
      };
    }
    return { ok: true, expirationMs: gameStartMs };
  }
  if (expiryMode === 'custom') {
    if (!customExpiry) {
      return { ok: false, error: 'Pick a custom expiry date/time first' };
    }
    // datetime-local is interpreted as local browser time; getTime() returns UTC ms.
    const ms = new Date(customExpiry).getTime();
    if (!Number.isFinite(ms) || ms <= nowMs) {
      return { ok: false, error: 'Custom expiry must be in the future' };
    }
    return { ok: true, expirationMs: ms };
  }
  return { ok: true, expirationMs: undefined };
}

/** Kalshi wire format. */
export function expirationTimeFrom(expirationMs: number | undefined): string | undefined {
  return expirationMs != null ? new Date(expirationMs).toISOString() : undefined;
}

/** Polymarket wire format — unix SECONDS, floored. */
export function expirationTsFrom(expirationMs: number | undefined): number | undefined {
  return expirationMs != null ? Math.floor(expirationMs / 1000) : undefined;
}

// ── Single-order payloads ─────────────────────────────────────────────────

export function buildKalshiOrderPayload(args: {
  ticker: string;
  exchangeIndex?: number;
  orderSide: MarketSide;
  orderType: 'limit' | 'market';
  count: number;
  priceCents: number;
  isMarket: boolean;
  expirationTime?: string;
}): OrderInput {
  const { ticker, exchangeIndex, orderSide, orderType, count, priceCents, isMarket, expirationTime } = args;
  return {
    ticker,
    ...(exchangeIndex !== undefined ? { exchangeIndex } : {}),
    side: orderSide,
    action: 'buy',
    type: orderType,
    count,
    ...(isMarket ? {} : { price: priceCents }),
    ...(expirationTime ? { expirationTime } : {}),
  };
}

export function buildPolyOrderPayload(args: {
  tokenId: string;
  priceCents: number;
  contracts: number;
  negRisk?: boolean;
  tickSize?: string;
  expirationTs?: number;
}): PlacePolyOrderRelayArgs {
  const { tokenId, priceCents, contracts, negRisk, tickSize, expirationTs } = args;
  return {
    tokenId,
    action: 'buy',
    priceCents,
    contracts,
    ...(negRisk ? { negRisk: true } : {}),
    ...(tickSize ? { tickSize } : {}),
    ...(expirationTs ? { expirationTs } : {}),
  };
}

/**
 * Polymarket US (F3). The ticket's price is the price of the SIDE being
 * traded; the relay converts a NO price to the venue's long-side price. TIF
 * comes from the expiry mode: GTC rests; GTS / Custom become a venue GTD
 * carrying the resolved `goodTillTime`; 'day' is the venue's DAY order. The
 * ticket is always a human — `manual: true` is the venue's required flag.
 * Quantity may be fractional where the market's minimumTradeQty allows.
 */
export function buildPolyUsOrderPayload(args: {
  marketSlug: string;
  orderSide: MarketSide;
  priceCents: number;
  contracts: number;
  expiryMode: 'gts' | 'gtc' | 'custom' | 'day';
  expirationMs?: number;
  tickSize?: string | number | null;
  postOnly?: boolean;
}): PlacePolymarketUsOrderArgs {
  const { marketSlug, orderSide, priceCents, contracts, expiryMode, expirationMs, tickSize, postOnly } = args;
  const tif: PlacePolymarketUsOrderArgs['tif'] =
    expiryMode === 'day' ? 'DAY' : expiryMode === 'gtc' || expirationMs == null ? 'GTC' : 'GTD';
  return {
    marketSlug,
    side: orderSide,
    action: 'buy',
    priceCents,
    contracts,
    tif,
    ...(tif === 'GTD' ? { goodTillTime: new Date(expirationMs as number).toISOString() } : {}),
    ...(postOnly ? { postOnly: true } : {}),
    ...(tickSize != null && tickSize !== '' ? { tickSize } : {}),
    manual: true,
  };
}

// ── Ladder ────────────────────────────────────────────────────────────────

export interface LadderPlacement {
  /** null for the base row (the ticket's own price/qty inputs). */
  id: string | null;
  team: TeamSide;
  ticker: string;
  exchangeIndex?: number;
  priceCents: number;
  qty: number;
}

/**
 * Turn the base row + extra rungs into placements, dropping any rung that fails
 * validation or has no resolvable market. Each rung resolves its OWN ticker so
 * one click can place on both teams' markets at the shared YES/NO side.
 */
export function resolveLadderPlacements(args: {
  selectedSide: TeamSide;
  price: string;
  qty: string;
  extraLegs: readonly LadderLeg[];
  tickerForTeam: (team: TeamSide) => string;
  exchangeIndexForTeam?: (team: TeamSide) => number | undefined;
}): { legs: LadderPlacement[]; invalidCount: number } {
  const { selectedSide, price, qty, extraLegs, tickerForTeam, exchangeIndexForTeam } = args;
  const rows: { id: string | null; team: TeamSide; price: string; qty: string }[] = [
    { id: null, team: selectedSide, price, qty },
    ...extraLegs.map((l) => ({ id: l.id, team: l.team, price: l.price, qty: l.qty })),
  ];
  const legs: LadderPlacement[] = [];
  let invalidCount = 0;
  for (const r of rows) {
    const v = validateLeg({ price: r.price, qty: r.qty });
    const t = tickerForTeam(r.team);
    if (v.ok && t) {
      const exchangeIndex = exchangeIndexForTeam?.(r.team);
      legs.push({
        id: r.id,
        team: r.team,
        ticker: t,
        ...(exchangeIndex !== undefined ? { exchangeIndex } : {}),
        priceCents: v.parsed.priceCents,
        qty: v.parsed.qty,
      });
    } else {
      invalidCount += 1;
    }
  }
  return { legs, invalidCount };
}

export function buildLadderLegPayload(args: {
  ticker: string;
  exchangeIndex?: number;
  orderSide: MarketSide;
  priceCents: number;
  qty: number;
  expirationTime?: string;
}): OrderInput {
  const { ticker, exchangeIndex, orderSide, priceCents, qty, expirationTime } = args;
  return {
    ticker,
    ...(exchangeIndex !== undefined ? { exchangeIndex } : {}),
    side: orderSide,
    action: 'buy',
    type: 'limit',
    count: qty,
    price: priceCents,
    ...(expirationTime ? { expirationTime } : {}),
  };
}

// ── Oscar sequence ────────────────────────────────────────────────────────

export interface OscarSequenceInputBody {
  op: 'overtake' | 'release_overtake';
  ticker: string;
  /** Local diagnostic context; accountStore strips it from the relay wire. */
  exchangeIndex?: number;
  side: MarketSide;
  take: { priceCents: number; count: number };
  blocker?: { count: number };
  expirationTs?: number;
  cancelOrderId?: string;
}

/**
 * The sequence body, minus the retry identity fields the caller stamps on.
 * It is built as its own value (rather than inline at the POST) because the
 * retry-identity hash is computed over exactly this object.
 *
 * The blocker carries a COUNT only — the relay recomputes its price server-side.
 */
export function buildOscarSequenceInput(args: {
  ticker: string;
  exchangeIndex?: number;
  orderSide: MarketSide;
  takeLeg: { priceCents: number; qty: number };
  blockerLeg: { qty: number } | undefined;
  expirationMs: number | undefined;
  cancelOrderId: string | null;
}): OscarSequenceInputBody {
  const { ticker, exchangeIndex, orderSide, takeLeg, blockerLeg, expirationMs, cancelOrderId } = args;
  return {
    op: cancelOrderId ? 'release_overtake' : 'overtake',
    ticker,
    ...(exchangeIndex !== undefined ? { exchangeIndex } : {}),
    side: orderSide,
    take: { priceCents: takeLeg.priceCents, count: takeLeg.qty },
    ...(blockerLeg ? { blocker: { count: blockerLeg.qty } } : {}),
    ...(expirationMs != null ? { expirationTs: Math.floor(expirationMs / 1000) } : {}),
    ...(cancelOrderId ? { cancelOrderId } : {}),
  };
}
